import React from 'react'
import { arrayOf, number, shape, string, oneOf, object, bool } from 'prop-types'
import {
  PlayButton,
  Highlight,
  ButtonGroup
} from '@/component/Primitive/Events'
import styles from './EventVideoSplitHero.module.scss'
import ResponsiveImage from '@/component/Primitive/ResponsiveImage'
import cx from 'classnames'
import Stack from '@/component/Primitive/Stack'
import Hide from '@/component/Primitive/Hide'
import Type from '@/component/Primitive/Type'
import Prose from '@/component/Primitive/Prose'

export const EventVideoSplitHero = ({
  title,
  subtitle,
  description,
  image,
  theme,
  youtubeId,
  inverse,
  buttonGroup
}) => {
  const { mobile: mobileImage, desktop: desktopImage } = image
  return (
    <div
      className={cx(
        styles.EventVideoSplitHero,
        theme === 'dark' && styles.dark,
        theme === 'grey' && styles.grey
      )}
    >
      <div
        className={cx(
          styles.EventVideoSplitHeroInner,
          inverse && styles.inverse
        )}
      >
        <div className={styles.ImagesWrapper}>
          <Image device="desktop" image={desktopImage} youtubeId={youtubeId} />
          <Image device="mobile" image={mobileImage} youtubeId={youtubeId} />
        </div>
        <div className={styles.ContentWrapper}>
          <div className={styles.Content}>
            <Stack gap="medium">
              <Stack gap="tiny">
                {title && (
                  <Type
                    as="h1"
                    size={['heading5', 'heading2']}
                    weight="medium"
                    className={styles.Title}
                  >
                    <Highlight>{title}</Highlight>
                  </Type>
                )}
                {subtitle && (
                  <Type
                    as="h2"
                    size={['body2', 'body3']}
                    weight="medium"
                    className={styles.Subtitle}
                  >
                    {subtitle}
                  </Type>
                )}
                {description && (
                  <Type size={['body2', 'body2']}>
                    <Prose
                      className={styles.Description}
                      dangerousHtml={description}
                    />
                  </Type>
                )}
              </Stack>

              {!!buttonGroup?.length && (
                <ButtonGroup group={buttonGroup} theme={theme} />
              )}
            </Stack>
          </div>
        </div>
      </div>
    </div>
  )
}

EventVideoSplitHero.propTypes = {
  title: string,
  subtitle: string,
  description: string,
  theme: string,
  youtubeId: string,
  inverse: bool,
  buttonGroup: object,
  image: shape({
    mobile: shape({
      src: string,
      srcSet: arrayOf(shape({ width: number, src: string })),
      sizes: arrayOf(string)
    }),
    desktop: shape({
      src: string,
      srcSet: arrayOf(shape({ width: number, src: string })),
      sizes: arrayOf(string)
    })
  })
}

const Image = ({ device, image, youtubeId }) => {
  const hideProps =
    device === 'mobile' ? { at: 'desktop' } : { below: 'desktop' }
  const className =
    device === 'mobile' ? styles.mobileImage : styles.desktopImage
  return (
    <Hide {...hideProps} className={styles.ImageWrapper}>
      {youtubeId && (
        <PlayButton className={styles.Play} youtubeId={youtubeId} />
      )}
      <ResponsiveImage className={cx(styles.Image, className)} {...image} />
    </Hide>
  )
}

Image.propTypes = {
  device: oneOf(['mobile', 'desktop']),
  image: object,
  youtubeId: string
}
